import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTenantPosture } from '@/api/cipp';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { ROUTES } from '@/lib/routes';
import {
  CheckChip, Chip, DmarcChip, EnforcementChip, IssueChips, MfaCoverage, ScoreBar,
} from './SecurityKit';

/**
 * Security summary on the CIPP tenant page — the same per-tenant posture the Security
 * dashboard shows (server/cipp/security.ts, hourly cache), plus the CA policy list.
 */
export function TenantSecurityPanel({ customerId }: { customerId: string }) {
  const { data, isLoading } = useTenantPosture(customerId);
  const p = data?.posture;

  return (
    <section className="mt-3 rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-divider px-4 py-2.5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Security</h2>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {data?.fetchedAt && <span>Gathered {fmtRelative(data.fetchedAt)}</span>}
          <Link to={ROUTES.cipp.dashboards.security} className="hover:text-foreground hover:underline">All tenants →</Link>
        </div>
      </div>

      {isLoading || data?.building ? (
        <div className="flex items-center gap-2 px-4 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Gathering security posture from CIPP…
        </div>
      ) : !p ? (
        <div className="px-4 py-4 text-xs text-muted-foreground">No posture data for this tenant.</div>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-3 lg:grid-cols-4">
          <Fact label="Secure score">
            <div className="w-40"><ScoreBar pct={p.secureScore?.pct} /></div>
          </Fact>
          <Fact label="MFA registered">
            <MfaCoverage mfa={p.mfa} />
            {p.mfa?.adminsNotRegistered ? <Chip tone="bad" className="ml-1.5">{p.mfa.adminsNotRegistered} admin w/o MFA</Chip> : null}
          </Fact>
          <Fact label="MFA enforced by">
            <EnforcementChip mfa={p.mfa} />
            {p.mfa?.unprotected ? <span className="ml-1.5 text-xs text-muted-foreground">{p.mfa.unprotected} user{p.mfa.unprotected === 1 ? '' : 's'} not covered</span> : null}
          </Fact>
          <Fact label={`Mail · ${p.mail?.domain ?? 'domain'}`}>
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title={p.mail?.notes.join('\n')}>
              DMARC <DmarcChip mail={p.mail} /> SPF <CheckChip status={p.mail?.spf} /> DKIM <CheckChip status={p.mail?.dkim} />
            </span>
            {p.mail?.dkim === 'fail' && p.mail.dkimFix && (
              <div className="mt-1.5 w-full text-[10.5px] text-muted-foreground" title="CNAME targets from Exchange's DKIM signing config">
                <div className="font-medium text-foreground">To enable DKIM, publish these CNAMEs:</div>
                {p.mail.dkimFix.map((c) => (
                  <div key={c.host} className="break-all font-mono">{c.host} → {c.value}</div>
                ))}
              </div>
            )}
          </Fact>
          <Fact label="Conditional access" wide>
            {!p.ca ? <span className="text-xs text-muted-foreground">—</span>
              : p.ca.policies.length === 0 ? <span className="text-xs text-muted-foreground">No policies{p.mfa?.enforcement === 'Security defaults' ? ' (security defaults on)' : ''}</span>
              : (
                <div className="flex flex-wrap gap-1">
                  {p.ca.policies.filter((x) => x.state !== 'disabled').map((x) => (
                    <Chip key={x.name} tone={x.state === 'enabled' ? 'info' : 'muted'} title={`${x.state === 'enabled' ? 'Enabled' : 'Report-only'}${x.controls.length ? ` · ${x.controls.join(', ')}` : ''}`}>
                      {x.name}{x.state === 'reportOnly' ? ' (report-only)' : ''}
                    </Chip>
                  ))}
                  {p.ca.disabled > 0 && <Chip tone="muted">{p.ca.disabled} disabled</Chip>}
                </div>
              )}
          </Fact>
          <Fact label="Issues" wide>
            <IssueChips issues={p.issues} max={8} />
          </Fact>
        </div>
      )}
    </section>
  );
}

function Fact({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2' : undefined}>
      <div className="mb-1 truncate text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center">{children}</div>
    </div>
  );
}
